import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Plus, ArrowLeft, ArrowRight, Check, Trash2, BookOpen, MapPin, Wand2, Send, Crown, Compass, Skull, Flag, Heart, Map as MapIcon, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import CaptureFeed from '../components/CaptureFeed';
import JourneyMapEditor from '../components/JourneyMapEditor';
import JourneyDashboard from '../components/JourneyDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Format a FastAPI/Pydantic error response into a human-readable string.
 *  - 422 errors come as { detail: [{ loc: [...], msg, type }, ...] }
 *  - Custom 4xx errors come as { detail: "string" }
 *  - Some errors come as plain string
 */
const formatApiError = (data, fallback = 'Erro') => {
  if (!data) return fallback;
  const d = data.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d) && d.length > 0) {
    // Pydantic validation array
    return d.map((e) => {
      const field = (e.loc || []).slice(-2).join('.');
      const msg = e.msg || 'inválido';
      return field ? `${field}: ${msg}` : msg;
    }).join(' · ');
  }
  if (typeof data === 'string' && data.trim()) return data;
  return fallback;
};

const GENRES = [
  { key: 'Aventura', emoji: '🧭', Icon: Compass },
  { key: 'Suspense', emoji: '🕯️', Icon: Skull },
  { key: 'Terror',   emoji: '👻', Icon: Skull },
  { key: 'Ação',     emoji: '⚔️',  Icon: Flag },
  { key: 'Romance',  emoji: '🌹', Icon: Heart },
];
const BLOOM = ['Lembrar','Entender','Aplicar','Analisar','Avaliar','Criar'];
const FORMATS = [
  { key: 'multiple_choice', label: 'Múltipla Escolha' },
  { key: 'essay',           label: 'Dissertativa' },
  { key: 'mixed',           label: 'Mista' },
];

// ── Datetime helpers for the access-window inputs ────────────────────────
// <input type="datetime-local"> works with "YYYY-MM-DDTHH:mm" in LOCAL time,
// while the backend persists ISO-8601 UTC. Convert on both sides.
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};
const localInputToIso = (v) => {
  if (!v) return null;
  const d = new Date(v);  // interpreted as local time
  return isNaN(d.getTime()) ? null : d.toISOString();
};
const formatWindowLabel = (starts, ends) => {
  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };
  const s = fmt(starts), e = fmt(ends);
  if (!s && !e) return null;
  if (s && e) return `${s} → ${e}`;
  if (s) return `A partir de ${s}`;
  return `Até ${e}`;
};

const emptyMission = (order) => ({
  order,
  title: `Missão ${order}`,
  question_format: 'multiple_choice',
  bloom_level: 'Aplicar',
  xp_reward: 20,
  coin_reward: 5,
  num_questions: 3,
  questions: [],
});

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-6">
    {[1,2,3,4,5].map((n) => (
      <div key={n} className="flex items-center gap-2 flex-1">
        <div className={`w-7 h-7 rounded-full grid place-items-center font-outfit font-black text-xs border-2 ${step===n ? 'bg-amber-300 border-slate-900 text-slate-900' : step>n ? 'bg-emerald-400 border-slate-900 text-white' : 'bg-slate-100 border-slate-300 text-slate-400'}`}>
          {step>n ? <Check className="w-3.5 h-3.5"/> : n}
        </div>
        <span className={`text-[9px] sm:text-xs font-outfit font-black tracking-widest uppercase ${step===n?'text-slate-900':'text-slate-400'} hidden sm:inline`}>
          {n===1?'Universo':n===2?'Missões':n===3?'Mapa':n===4?'História':'Publicar'}
        </span>
        {n<5 && <div className={`flex-1 h-0.5 ${step>n?'bg-emerald-400':'bg-slate-200'}`}/>}
      </div>
    ))}
  </div>
);

// ─── Wizard component ───────────────────────────────────────────────────
export const JourneyWizard = ({ open, onClose, classroomId, onCreated, editing }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [generatingStory, setGeneratingStory] = useState(false);
  const [storyPreview, setStoryPreview] = useState(null);
  const [journeyId, setJourneyId] = useState(editing?.journey_id || null);

  const [form, setForm] = useState(() => ({
    title: editing?.title || '',
    scenario_map: editing?.scenario_map || 'village_default',
    map_image_url: editing?.map_image_url || null,
    content: editing?.content || '',
    genre: editing?.genre || 'Aventura',
    num_missions: editing?.num_missions || 3,
    missions: editing?.missions?.length ? editing.missions : [emptyMission(1), emptyMission(2), emptyMission(3)],
    // Access window: ISO stored on the server, but <input type="datetime-local">
    // expects "YYYY-MM-DDTHH:mm" without timezone. Convert on load / save.
    starts_at: editing?.starts_at ? isoToLocalInput(editing.starts_at) : '',
    ends_at: editing?.ends_at ? isoToLocalInput(editing.ends_at) : '',
  }));

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep(1); setStoryPreview(null); setGeneratedMissions([]); setJourneyId(editing?.journey_id || null);
      return;
    }
    // When opening for an existing journey that already has a story/questions, pre-fill the preview
    if (editing?.story_intro && editing?.missions?.length) {
      setStoryPreview({
        intro: editing.story_intro,
        climax_success: editing.climax_success,
        climax_failure: editing.climax_failure,
        missions: editing.missions.map((m) => ({
          order: m.order,
          story_success: m.story_success,
          story_failure: m.story_failure,
        })),
      });
      setGeneratedMissions(editing.missions);
    }
  }, [open, editing]);

  // Keep missions array in sync with num_missions
  const setNumMissions = (n) => {
    const next = Math.max(1, Math.min(10, Number(n) || 1));
    setForm((f) => {
      const m = [...f.missions];
      while (m.length < next) m.push(emptyMission(m.length + 1));
      while (m.length > next) m.pop();
      return { ...f, num_missions: next, missions: m.map((mm, i) => ({ ...mm, order: i + 1 })) };
    });
  };

  const updateMission = (idx, patch) => {
    setForm((f) => {
      const m = [...f.missions];
      m[idx] = { ...m[idx], ...patch };
      return { ...f, missions: m };
    });
  };

  // Holds the per-mission AI-generated questions for the preview step
  const [generatedMissions, setGeneratedMissions] = useState([]);
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);

  const validateStep1 = () => {
    if (!form.title.trim()) return 'Defina um título';
    if (!form.content.trim()) return 'Descreva o conteúdo curricular';
    if (!GENRES.find((g) => g.key === form.genre)) return 'Gênero inválido';
    // Window: both optional, but if both are set the end must be after the start.
    if (form.starts_at && form.ends_at) {
      const s = new Date(form.starts_at).getTime();
      const e = new Date(form.ends_at).getTime();
      if (isNaN(s) || isNaN(e)) return 'Datas de acesso inválidas';
      if (e <= s) return 'A data de encerramento deve ser após a data de início';
    }
    return null;
  };
  const validateStep2 = () => {
    for (const m of form.missions) {
      if (!m.title.trim()) return `Missão ${m.order}: título obrigatório`;
      const nq = Number(m.num_questions) || 0;
      if (nq < 1 || nq > 8) return `Missão ${m.order}: quantidade de questões entre 1 e 8`;
    }
    return null;
  };

  const persistDraft = async () => {
    const payload = {
      classroom_id: classroomId,
      title: form.title.trim(),
      scenario_map: form.scenario_map,
      map_image_url: form.map_image_url,
      content: form.content.trim(),
      genre: form.genre,
      num_missions: form.num_missions,
      missions: form.missions,
      npcs: [], // FASE C
      starts_at: localInputToIso(form.starts_at),
      ends_at: localInputToIso(form.ends_at),
    };
    setSaving(true);
    try {
      if (!journeyId) {
        const r = await fetch(`${BACKEND_URL}/api/teacher/journeys`, {
          method: 'POST', credentials: 'include',
          headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(formatApiError(data, 'Erro ao criar jornada'));
        setJourneyId(data.journey_id);
        toast.success('Rascunho criado!', { icon: '📜' });
        return data.journey_id;
      } else {
        const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}`, {
          method: 'PUT', credentials: 'include',
          headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(formatApiError(err, 'Erro ao atualizar'));
        }
        toast.success('Rascunho salvo', { icon: '💾' });
        return journeyId;
      }
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) { toast.error(err); return; }
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) { toast.error(err); return; }
      const jid = await persistDraft();
      if (jid) setStep(3);
    } else if (step === 3) {
      // Persist map positions before moving on
      const jid = await persistDraft();
      if (jid) setStep(4);
    } else if (step === 4) {
      setStep(5);
    }
  };

  const handleGenerateStory = async () => {
    let jid = journeyId;
    if (!jid) {
      jid = await persistDraft();
      if (!jid) return;
    }
    setGeneratingStory(true);
    setStoryPreview(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${jid}/generate-story`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro');
      setStoryPreview(data.story);
      setGeneratedMissions(data.missions || []);
      toast.success('História + questões geradas pela IA!', { icon: '✨' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGeneratingStory(false);
    }
  };

  const handleRegenerateQuestions = async (missionId, idx) => {
    if (!journeyId || !missionId) return;
    setRegeneratingIdx(idx);
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}/missions/${missionId}/regenerate-questions`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro');
      setGeneratedMissions((arr) => arr.map((m) => m.mission_id === missionId ? { ...m, questions: data.questions } : m));
      toast.success('Questões regeradas!', { icon: '🔄' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRegeneratingIdx(null);
    }
  };

  const handlePublish = async () => {
    if (!journeyId) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}/publish`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao publicar');
      }
      toast.success('Jornada publicada para a turma!', { icon: '🚀' });
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="journey-wizard">
        <DialogHeader>
          <DialogTitle className="font-outfit text-2xl flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-amber-500" /> {editing ? 'Editar Jornada' : 'Nova Jornada Narrativa'}
          </DialogTitle>
          <DialogDescription className="font-nunito text-xs text-slate-500">
            Crie uma campanha de RPG pedagógico em 4 passos.
          </DialogDescription>
        </DialogHeader>

        <Stepper step={step} />

        {/* STEP 1 — UNIVERSO */}
        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div>
              <label className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Título da Jornada</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder="Ex: O Mistério da Floresta Fotossintética"
                data-testid="wizard-title"
              />
            </div>
            <div>
              <label className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Cenário do Mapa</label>
              <Select value={form.scenario_map} onValueChange={(v) => setForm({...form, scenario_map: v})}>
                <SelectTrigger data-testid="wizard-scenario"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="village_default">Vilarejo Verde (padrão)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 italic mt-1">Mais cenários virão em breve. Por enquanto, todos usam o mapa padrão.</p>
            </div>
            <div>
              <label className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Conteúdo Curricular</label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({...form, content: e.target.value})}
                placeholder="Descreva o conteúdo científico que será trabalhado nesta jornada (ex: 'Fotossíntese: clorofila, luz solar, CO2, água, glicose, oxigênio'). A IA vai usar isso para construir a narrativa."
                rows={4}
                data-testid="wizard-content"
              />
            </div>
            <div>
              <label className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Gênero Narrativo</label>
              <div className="grid grid-cols-5 gap-2 mt-1" data-testid="wizard-genre">
                {GENRES.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setForm({...form, genre: g.key})}
                    data-testid={`wizard-genre-${g.key}`}
                    className={`p-2 rounded-xl border-2 font-outfit font-bold text-xs transition-all ${form.genre===g.key?'bg-amber-100 border-slate-900 -translate-y-0.5 shadow-md':'bg-white border-slate-300 hover:bg-slate-50'}`}
                  >
                    <div className="text-xl">{g.emoji}</div>
                    {g.key}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Quantidade de Missões: {form.num_missions}</label>
              <input
                type="range" min={1} max={10} value={form.num_missions}
                onChange={(e) => setNumMissions(e.target.value)}
                className="w-full accent-amber-500"
                data-testid="wizard-num-missions"
              />
            </div>

            {/* Access window — students only see/play within this range */}
            <div className="rounded-2xl border-2 border-slate-200 bg-slate-50/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500">Período de acesso</span>
                <Badge variant="secondary" className="text-[10px]">Opcional</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="wizard-starts-at" className="text-[11px] font-outfit font-bold text-slate-600">Início</label>
                  <Input
                    id="wizard-starts-at"
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                    data-testid="wizard-starts-at"
                  />
                </div>
                <div>
                  <label htmlFor="wizard-ends-at" className="text-[11px] font-outfit font-bold text-slate-600">Encerramento</label>
                  <Input
                    id="wizard-ends-at"
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                    data-testid="wizard-ends-at"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 italic mt-2">
                Alunos só conseguem entrar na jornada dentro deste período. Deixe em branco para não restringir.
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 — MISSÕES (metadata only; questions are AI-generated) */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <div className="rounded-xl border-2 border-violet-200 bg-violet-50 p-3 text-xs text-slate-700 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5"/>
              <p>Defina apenas a <strong>identidade</strong> de cada missão (título, formato, nível de Bloom, recompensas e quantidade de questões). <strong>A IA escreverá as questões</strong> no próximo passo, alinhadas ao conteúdo e ao gênero da jornada.</p>
            </div>
            <p className="text-xs text-slate-600 italic">A última missão será automaticamente o clímax (👑). Cada missão fica trancada até que a anterior esteja concluída.</p>
            {form.missions.map((m, idx) => (
              <div key={idx} className="border-2 border-slate-200 rounded-xl p-3 bg-slate-50" data-testid={`wizard-mission-${idx}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="font-outfit font-black text-sm flex items-center gap-2">
                    {idx === form.missions.length - 1 ? <Crown className="w-4 h-4 text-amber-500"/> : <MapPin className="w-4 h-4 text-violet-500"/>}
                    Missão {idx+1}
                  </h4>
                </div>
                <Input
                  placeholder="Título da missão (ex: A Câmara das Folhas Mortas)"
                  value={m.title}
                  onChange={(e) => updateMission(idx, {title: e.target.value})}
                  className="mb-2"
                  data-testid={`mission-title-${idx}`}
                />
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div>
                    <label className="text-[10px] uppercase font-black text-slate-500">Formato</label>
                    <Select value={m.question_format} onValueChange={(v) => updateMission(idx, {question_format: v})}>
                      <SelectTrigger className="text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{FORMATS.map((f)=><SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black text-slate-500">Bloom</label>
                    <Select value={m.bloom_level} onValueChange={(v) => updateMission(idx, {bloom_level: v})}>
                      <SelectTrigger className="text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{BLOOM.map((b)=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black text-slate-500">Nº Questões</label>
                    <Input type="number" min={1} max={8} value={m.num_questions} onChange={(e)=>updateMission(idx,{num_questions: Math.max(1, Math.min(8, Number(e.target.value)||1))})} className="text-xs" data-testid={`mission-numq-${idx}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black text-slate-500">AURA</label>
                    <Input type="number" min={0} value={m.xp_reward} onChange={(e)=>updateMission(idx,{xp_reward: Number(e.target.value)||0})} className="text-xs"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black text-slate-500">Coins</label>
                    <Input type="number" min={0} value={m.coin_reward} onChange={(e)=>updateMission(idx,{coin_reward: Number(e.target.value)||0})} className="text-xs"/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 3 — MAPA DA JORNADA (Drag & Drop pins) */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-xs text-slate-700 flex items-start gap-2">
              <MapIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"/>
              <p>Desenhe a <strong>rota da jornada</strong>: arraste cada pin para o local da missão no mapa. A linha tracejada conecta-os na ordem de progressão. Você pode usar o mapa padrão ou enviar o seu.</p>
            </div>
            <JourneyMapEditor
              mapImageUrl={form.map_image_url}
              missions={form.missions}
              onChange={({ mapImageUrl, missions }) => setForm((f) => ({ ...f, map_image_url: mapImageUrl, missions }))}
            />
          </div>
        )}

        {/* STEP 4 — STORYTELLING */}
        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-step-4">
            <div className="bg-gradient-to-br from-violet-50 to-amber-50 border-2 border-violet-300 rounded-xl p-4 text-center">
              <Wand2 className="w-10 h-10 mx-auto text-violet-500 mb-2" />
              <p className="font-outfit font-black text-sm">A IA vai escrever a narrativa</p>
              <p className="text-xs text-slate-600 italic mt-1">Gemini 3 Flash adapta o conteúdo ao gênero <strong>{form.genre}</strong>, gerando abertura, ganchos por missão e clímax (sucesso / derrota).</p>
              <Button onClick={handleGenerateStory} disabled={generatingStory} className="mt-3" data-testid="wizard-generate-story">
                {generatingStory ? <Sparkles className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
                {generatingStory ? 'Escrevendo…' : (storyPreview ? 'Regenerar' : 'Gerar História')}
              </Button>
            </div>
            {storyPreview && (
              <div className="space-y-3 text-sm">
                <div className="rounded-xl border-2 border-slate-200 p-3">
                  <p className="text-xs font-outfit font-black uppercase tracking-widest text-slate-500 mb-1">Abertura</p>
                  <p className="font-nunito text-slate-800">{storyPreview.intro}</p>
                </div>
                {(storyPreview.missions || []).map((m, i) => {
                  const meta = generatedMissions.find((x) => x.order === m.order) || {};
                  return (
                    <div key={i} className="rounded-xl border-2 border-slate-200 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-outfit font-black uppercase tracking-widest text-slate-700">
                          Missão {m.order} · {meta.title || ''}
                        </p>
                        {meta.mission_id && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={regeneratingIdx === i}
                            onClick={() => handleRegenerateQuestions(meta.mission_id, i)}
                            className="h-7 text-[11px]"
                            data-testid={`regen-questions-${i}`}
                          >
                            {regeneratingIdx === i ? <Sparkles className="w-3 h-3 mr-1 animate-spin"/> : <Sparkles className="w-3 h-3 mr-1"/>}
                            Regerar questões
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] font-outfit font-black uppercase tracking-widest text-emerald-600">Acerto</p>
                      <p className="font-nunito text-slate-800 text-xs">{m.story_success}</p>
                      <p className="text-[11px] font-outfit font-black uppercase tracking-widest text-rose-600">Erro</p>
                      <p className="font-nunito text-slate-700 italic text-xs">{m.story_failure}</p>
                      {/* AI generated questions preview */}
                      {(meta.questions || []).length > 0 && (
                        <details className="rounded-lg border border-slate-200 bg-slate-50">
                          <summary className="cursor-pointer px-2 py-1.5 text-[11px] font-outfit font-black uppercase tracking-widest text-slate-600">
                            {meta.questions.length} questões geradas
                          </summary>
                          <div className="p-2 space-y-2">
                            {meta.questions.map((q, qi) => (
                              <div key={qi} className="text-xs bg-white rounded-md p-2 border border-slate-200">
                                <p className="font-semibold text-slate-800">{qi+1}. {q.question_text}</p>
                                {q.question_type === 'multiple_choice' && (
                                  <ul className="mt-1 ml-3 space-y-0.5">
                                    {['a','b','c','d'].map((l) => (
                                      <li key={l} className={`${q.correct_answer === l.toUpperCase() ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>
                                        {l.toUpperCase()}) {q[`option_${l}`]}
                                        {q.correct_answer === l.toUpperCase() && <span className="ml-1">✓</span>}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {q.question_type === 'essay' && (
                                  <p className="mt-1 text-[11px] italic text-slate-500">
                                    Palavras-chave esperadas: {(q.expected_keywords || []).join(', ') || '—'}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3">
                  <p className="text-xs font-outfit font-black uppercase tracking-widest text-emerald-700 mb-1">Clímax (Sucesso)</p>
                  <p className="font-nunito text-slate-800">{storyPreview.climax_success}</p>
                </div>
                <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3">
                  <p className="text-xs font-outfit font-black uppercase tracking-widest text-rose-700 mb-1">Clímax (Falha)</p>
                  <p className="font-nunito text-slate-800">{storyPreview.climax_failure}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 5 — PUBLISH */}
        {step === 5 && (
          <div className="space-y-4" data-testid="wizard-step-5">
            <div className="bg-gradient-to-br from-emerald-50 to-amber-50 border-2 border-emerald-300 rounded-xl p-4 text-center">
              <h4 className="font-outfit font-black text-lg">Tudo pronto?</h4>
              <p className="text-sm text-slate-700 mt-1">
                <strong>{form.missions.length}</strong> missões · gênero <strong>{form.genre}</strong>
              </p>
              <p className="text-xs text-slate-600 italic mt-2">
                Após publicar, a jornada aparece para todos os alunos da turma.
                Você poderá editar / arquivar depois.
              </p>
              <Button
                onClick={handlePublish}
                disabled={saving}
                className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                data-testid="wizard-publish"
              >
                <Send className="w-4 h-4 mr-2"/> Publicar Jornada
              </Button>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="flex justify-between mt-4 pt-3 border-t border-slate-200">
          <Button variant="ghost" onClick={() => step>1 ? setStep(step-1) : onClose()} disabled={saving} data-testid="wizard-back">
            <ArrowLeft className="w-4 h-4 mr-1"/> {step>1?'Voltar':'Cancelar'}
          </Button>
          {step < 5 && (
            <Button onClick={handleNext} disabled={saving} className="bg-amber-400 hover:bg-amber-500 text-slate-900" data-testid="wizard-next">
              {saving ? 'Salvando…' : 'Avançar'} <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Hub page: list + create ───────────────────────────────────────────
export default function TeacherJourneys() {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dashboardJid, setDashboardJid] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys?classroom_id=${classroomId}`, { credentials: 'include' });
      const d = await r.json();
      setJourneys(d.journeys || []);
    } catch (e) {
      toast.error('Não foi possível carregar jornadas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [classroomId]);  // eslint-disable-line

  const handleEdit = async (j) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${j.journey_id}`, { credentials: 'include' });
      const d = await r.json();
      setEditing(d);
      setOpen(true);
    } catch { toast.error('Erro ao abrir'); }
  };

  const handleDelete = async (j) => {
    if (!window.confirm(`Excluir "${j.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/teacher/journeys/${j.journey_id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Jornada removida');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="teacher-journeys-hub">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/teacher/classroom/${classroomId}`)} data-testid="back-classroom-btn">
              <ArrowLeft className="w-5 h-5"/>
            </Button>
            <div>
              <h1 className="font-outfit font-black text-xl">Centro das Jornadas</h1>
              <p className="text-xs text-slate-500">Crie campanhas narrativas de RPG pedagógico</p>
            </div>
          </div>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold" data-testid="new-journey-btn">
            <Plus className="w-4 h-4 mr-1"/> Nova Jornada
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          {loading ? (
            <p className="text-center text-slate-500">Carregando…</p>
          ) : journeys.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-300">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3"/>
              <p className="font-outfit font-black text-lg text-slate-700">Nenhuma jornada criada ainda</p>
              <p className="text-sm text-slate-500 italic mt-1">Comece criando sua primeira campanha narrativa.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="journeys-grid">
              {journeys.map((j) => (
                <div key={j.journey_id} className="bg-white rounded-2xl border-2 border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`journey-card-${j.journey_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-outfit font-black text-lg text-slate-900 truncate flex-1">{j.title}</h3>
                    <Badge
                      className={
                        j.status === 'closed' ? 'bg-rose-500 text-white' :
                        j.status === 'active' ? 'bg-emerald-500 text-white' :
                        j.status === 'archived' ? 'bg-slate-400 text-white' :
                        'bg-slate-200 text-slate-700'
                      }
                    >
                      {j.status === 'closed' ? 'Encerrada' :
                       j.status === 'active' ? 'Ativa' :
                       j.status === 'archived' ? 'Arquivada' : 'Rascunho'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    <span className="font-bold">{j.num_missions}</span> missões · {j.genre}
                  </p>
                  {formatWindowLabel(j.starts_at, j.ends_at) && (
                    <p
                      className="text-[11px] text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-3 inline-flex items-center gap-1"
                      data-testid={`journey-window-${j.journey_id}`}
                    >
                      <MapIcon className="w-3 h-3 text-amber-700" />
                      {formatWindowLabel(j.starts_at, j.ends_at)}
                    </p>
                  )}
                  <div className="flex gap-2">
                    {(j.status === 'active' || j.status === 'closed') && (
                      <Button size="sm" onClick={() => setDashboardJid(j.journey_id)} className="flex-1 bg-amber-400 hover:bg-amber-500 text-slate-900" data-testid={`dashboard-journey-${j.journey_id}`}>
                        <Trophy className="w-4 h-4 mr-1"/> Painel
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleEdit(j)} className="flex-1" data-testid={`edit-journey-${j.journey_id}`}>
                      Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(j)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-journey-${j.journey_id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mural de Capturas — live feed */}
        <CaptureFeed classroomId={classroomId} />
      </div>

      <JourneyWizard
        open={open}
        onClose={() => setOpen(false)}
        classroomId={classroomId}
        onCreated={load}
        editing={editing}
      />

      <JourneyDashboard
        open={!!dashboardJid}
        journeyId={dashboardJid}
        onClose={() => setDashboardJid(null)}
        onChanged={load}
      />
    </div>
  );
}
